/**
 * Shared group master-detail shell (spec 0103). A lightweight nav-list of
 * product groups sits on the left; the right pane is whatever the caller passes
 * as `children` — records the caller has *already* filtered by the current
 * selection. An "All" row leads the nav-list and is selected whenever
 * `selectedKey` is null, so the right pane shows everything until a group is
 * picked.
 *
 * Selection is conveyed by MUI's themed `ListItemButton` `selected` state (plus
 * `aria-current` so it isn't colour-only): no hardcoded colours, so white-label
 * theming carries through untouched. This component owns layout and selection
 * styling only — it holds no state and fetches nothing.
 */
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useCopy } from '@/hooks/useCopy';

export interface GroupMasterDetailGroup {
  key: string;
  label: string;
  count: number;
}

export interface GroupMasterDetailProps {
  groups: GroupMasterDetailGroup[];
  /** `null` selects the leading "All" row. */
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  /** Label for the "All" row, e.g. "All tenants". */
  allLabel: string;
  allCount: number;
  /** The right pane — records already filtered by the parent. */
  children: ReactNode;
}

export function GroupMasterDetail({
  groups,
  selectedKey,
  onSelect,
  allLabel,
  allCount,
  children,
}: GroupMasterDetailProps) {
  const { t } = useCopy();

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
      <Paper variant="outlined" sx={{ flex: 'none', width: { xs: '100%', md: 240 } }}>
        <List
          dense
          subheader={<ListSubheader disableSticky>{t('common.groups', 'Groups')}</ListSubheader>}
        >
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedKey === null}
              aria-current={selectedKey === null ? 'true' : undefined}
              onClick={() => onSelect(null)}
            >
              <ListItemText primary={allLabel} />
              <GroupCount value={allCount} />
            </ListItemButton>
          </ListItem>
          {groups.map((g) => (
            <ListItem key={g.key} disablePadding>
              <ListItemButton
                selected={selectedKey === g.key}
                aria-current={selectedKey === g.key ? 'true' : undefined}
                onClick={() => onSelect(g.key)}
              >
                <ListItemText primary={g.label} />
                <GroupCount value={g.count} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>{children}</Box>
    </Stack>
  );
}

/** Trailing muted record count for a nav-list row. */
function GroupCount({ value }: { value: number }) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        ml: 1,
      }}
    >
      {value}
    </Typography>
  );
}
